import React, { FC, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography, LinearProgress } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useStartSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
  useGetContentQuery,
} from "@store/gameApi";
import { RootState } from "@store/store";
import {
  getLevelParams,
  resolveRoundContent,
  computeRoundMetrics,
  computeCategoryTapErrorCount,
  computeCategoryTapReactionTime,
  type CategoryTapRoundContent,
  type StimulusStep,
  type CategoryTapItemResponse,
} from "./categoryTapContent";
import {
  getContentBankLabel,
  getContentBankCategoryLabel,
  getContentBankVoiceDescription,
} from "@utils/contentBankTranslations";
import {
  gameContinueButtonSx,
  gamePrimaryButtonSx,
  gameSecondaryButtonSx,
} from "@components/Home/Games/gameUi";
import { useGamePause } from "@hooks/useGamePause";
import { useGameTimingMode } from "@hooks/useGameTimingMode";
import { useInGameMoreTimeAllowed } from "@hooks/useInGameMoreTimeAllowed";
import {
  adjustCategoryTapTiming,
  getInGameMoreTimeBonusMs,
} from "@utils/gameTiming";
import {
  buildImageCandidateUrls,
  preloadImageUrls,
} from "@utils/gameImage";
import GameAvatarOverlay, {
  GameAvatarOverlayHandle,
} from "@components/GameAvatarOverlay";
import { useVoiceOverPref } from "@hooks/useVoiceOverPref";

export interface CategoryTapGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;
const CATEGORY_SWITCH_PAUSE_MS = 1000;

type Phase = "idle" | "playing" | "switchPause" | "feedback";

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

type ItemResponse = CategoryTapItemResponse;

const CategoryTap: FC<CategoryTapGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const lastSessionStartAtRef = useRef(0);
  const [startSession] = useStartSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();

  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;
  const gameTimingMode = useGameTimingMode();
  const inGameMoreTimeAllowed = useInGameMoreTimeAllowed();
  const levelParams = useMemo(
    () => adjustCategoryTapTiming(getLevelParams(difficulty), gameTimingMode),
    [difficulty, gameTimingMode]
  );

  const contentSeedForFetch =
    contentSeed ||
    (effectiveSessionId
      ? `${effectiveSessionId}-${GAME_IDS.categoryTap}`
      : "");
  const { data: contentData, error: contentError } = useGetContentQuery(
    { gameId: GAME_IDS.categoryTap, seed: contentSeedForFetch },
    { skip: !contentSeedForFetch }
  );

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [roundContent, setRoundContent] = useState<CategoryTapRoundContent | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [responses, setResponses] = useState<ItemResponse[]>([]);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [xpEarned, setXpEarned] = useState<number | null>(null);
  const { paused, timerFrozen, togglePause, resumeTimerOnPlay, isTimerFrozen, gameNow } =
    useGamePause();
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<
    { accuracy: number; completionMs: number; avgReactionMs: number | null }[]
  >([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);
  const [voiceOverEnabled, setVoiceOverEnabled] = useVoiceOverPref(
    GAME_IDS.categoryTap
  );
  const [avatarReady, setAvatarReady] = useState(false);
  const [stepBonusMs, setStepBonusMs] = useState(0);
  const [moreTimeUsedForStep, setMoreTimeUsedForStep] = useState(false);
  const [imageAttemptById, setImageAttemptById] = useState<Record<string, number>>(
    {}
  );
  const submittedForRoundRef = useRef(false);
  const sessionSeedRef = useRef<string | null>(null);
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const switchPauseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const voiceDescriptionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const avatarRef = useRef<GameAvatarOverlayHandle | null>(null);
  const stepStartRef = useRef(0);

  const sessionSeed = useMemo(() => {
    if (sessionSeedRef.current) return sessionSeedRef.current;
    sessionSeedRef.current = `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    return sessionSeedRef.current;
  }, []);

  const currentStep: StimulusStep | null =
    roundContent ? (roundContent.sequence[currentStepIndex] ?? null) : null;
  const showSwitchPause: StimulusStep | null =
    phase === "switchPause" && roundContent
      ? (roundContent.sequence[currentStepIndex] ?? null)
      : null;

  const startNewRound = useCallback(
    (roundIndex?: number) => {
      resumeTimerOnPlay();
      submittedForRoundRef.current = false;
      const idx = roundIndex ?? roundCount;
      const content = resolveRoundContent(
        contentData,
        contentError,
        contentSeed,
        idx,
        levelParams,
        sessionSeed
      );
      setRoundContent(content);
      setImageAttemptById({});
      setCurrentStepIndex(0);
      setResponses([]);
      setRoundStartTime(gameNow());
      setXpEarned(null);
      if (content) {
        setPhase("playing");
      }
    },
    [contentSeed, roundCount, levelParams, contentData, contentError, sessionSeed, gameNow, resumeTimerOnPlay]
  );

  useEffect(() => {
    if (!roundContent) return;
    const urls = roundContent.sequence.flatMap((step) =>
      buildImageCandidateUrls(step.item.imageUrl)
    );
    const controller = new AbortController();
    preloadImageUrls(urls, { concurrency: 4, signal: controller.signal }).catch(
      () => {}
    );
    return () => controller.abort();
  }, [roundContent]);

  const advanceToNext = useCallback(
    (tapped: boolean, missed = false) => {
      if (!roundContent) return;
      resumeTimerOnPlay();
      const step = roundContent.sequence[currentStepIndex];
      if (!step) return;
      if (voiceDescriptionTimerRef.current) {
        clearTimeout(voiceDescriptionTimerRef.current);
        voiceDescriptionTimerRef.current = null;
      }
      avatarRef.current?.stop();

      // Missed items (timer auto-advanced) don't have a meaningful reaction
      // time. For taps and deliberate skips we record ms since the stimulus
      // appeared, minus any "more time" bonus so reaction is measured from
      // the effective decision window. If `stepStartRef` was never stamped
      // (e.g. tap fires before the step-start effect runs), fall back to
      // null so we don't leak a wall-clock value to the backend.
      const stepStart = stepStartRef.current;
      const elapsed = stepStart > 0 ? gameNow() - stepStart - stepBonusMs : -1;
      // Guard against clock weirdness: cap at a generous 60s. Anything
      // above that is almost certainly a measurement bug.
      const reactionTime =
        missed || elapsed < 0 || elapsed > 60_000
          ? null
          : Math.max(0, elapsed);
      const nextResponse: ItemResponse = { tapped, missed, reactionTime };
      setResponses((prev) => [...prev, nextResponse]);
      const nextIndex = currentStepIndex + 1;

      if (nextIndex >= roundContent.sequence.length) {
        setPhase("feedback");
        if (!submittedForRoundRef.current) {
          submittedForRoundRef.current = true;
          const allResponses = [...responses, nextResponse];
          const { accuracy, switchScore, missedItems } = computeRoundMetrics(
            roundContent.sequence,
            allResponses
          );
          const { avgReactionMs, reactionTimeMs } =
            computeCategoryTapReactionTime(allResponses);
          const completionTimeMs = gameNow() - roundStartTime;
          const errorCount = computeCategoryTapErrorCount(
            roundContent.sequence.length,
            accuracy,
            missedItems,
          );
          setRoundResults((prev) => [
            ...prev,
            {
              accuracy,
              completionMs: completionTimeMs,
              avgReactionMs: reactionTimeMs != null ? avgReactionMs : null,
            },
          ]);

          if (sessionId) {
            submitRound({
              sessionId,
              body: {
                gameId: GAME_IDS.categoryTap,
                roundNumber: roundCount + 1,
                difficulty,
                accuracy,
                reactionTimeMs,
                completionTimeMs,
                errorCount,
                rawData: {
                  switchScore,
                  missedItems,
                  avgReactionMs: reactionTimeMs != null ? avgReactionMs : null,
                  itemResponses: allResponses,
                },
              },
            })
              .unwrap()
              .then((res) => {
                if (res?.xpEarned != null) {
                  setXpEarned(res.xpEarned);
                  setSessionScore((s) => s + res.xpEarned);
                }
                if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
                  setDomainUpdatesSum((prev) => {
                    const next = { ...prev };
                    for (const [k, v] of Object.entries(res.domainUpdates)) {
                      next[k] = (next[k] ?? 0) + v;
                    }
                    return next;
                  });
                }
              })
              .catch(() => {});
          } else {
            const xp = accuracy >= 0.8 ? 20 : 10;
            setXpEarned(xp);
            setSessionScore((s) => s + xp);
          }
        }
        return;
      }

      const nextStep = roundContent.sequence[nextIndex];
      const categorySwitched =
        nextStep && nextStep.phaseIndex !== step.phaseIndex;

      if (categorySwitched) {
        setCurrentStepIndex(nextIndex);
        setPhase("switchPause");
        if (switchPauseTimerRef.current) clearTimeout(switchPauseTimerRef.current);
        switchPauseTimerRef.current = setTimeout(() => {
          switchPauseTimerRef.current = null;
          setPhase("playing");
        }, CATEGORY_SWITCH_PAUSE_MS);
        // Optional ding on category switch (respects muted)
        if (typeof window !== "undefined" && window.AudioContext && !muted) {
          try {
            const ctx = new window.AudioContext();
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.frequency.value = 880;
            osc.type = "sine";
            gain.gain.setValueAtTime(0.15, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.15);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.15);
          } catch {
            // ignore
          }
        }
      } else {
        setCurrentStepIndex(nextIndex);
      }
    },
    [
      roundContent,
      currentStepIndex,
      responses,
      roundStartTime,
      sessionId,
      roundCount,
      difficulty,
      submitRound,
      muted,
      gameNow,
      resumeTimerOnPlay,
    ]
  );

  const handleTap = useCallback(() => {
    if (phase !== "playing" || !currentStep) return;
    resumeTimerOnPlay();
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    advanceToNext(true);
  }, [phase, currentStep, advanceToNext, resumeTimerOnPlay]);

  const handleSkip = useCallback(() => {
    if (phase !== "playing" || !currentStep) return;
    resumeTimerOnPlay();
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    advanceToNext(false);
  }, [phase, currentStep, advanceToNext, resumeTimerOnPlay]);

  useEffect(() => {
    setStepBonusMs(0);
    setMoreTimeUsedForStep(false);
  }, [currentStepIndex, roundCount]);

  const scheduleStepAutoAdvance = useCallback(() => {
    if (autoAdvanceTimerRef.current) {
      clearTimeout(autoAdvanceTimerRef.current);
      autoAdvanceTimerRef.current = null;
    }
    if (phase !== "playing" || !roundContent || !currentStep) return;
    if (isTimerFrozen()) return;
    const ms = roundContent.levelParams.timePerItemMs;
    if (ms <= 0) return;
    const remaining = Math.max(
      0,
      stepStartRef.current + ms + stepBonusMs - gameNow()
    );
    autoAdvanceTimerRef.current = setTimeout(() => {
      autoAdvanceTimerRef.current = null;
      advanceToNext(false, true);
    }, remaining);
  }, [
    phase,
    roundContent,
    currentStep,
    stepBonusMs,
    advanceToNext,
    isTimerFrozen,
    gameNow,
  ]);

  // Stamp step start before auto-advance schedules (layout effects run in order).
  useLayoutEffect(() => {
    if (phase !== "playing" || !roundContent || !currentStep) return;
    if (stepBonusMs === 0) {
      stepStartRef.current = gameNow();
    }
  }, [phase, roundContent, currentStepIndex, currentStep, stepBonusMs, gameNow]);

  useLayoutEffect(() => {
    scheduleStepAutoAdvance();
    return () => {
      if (autoAdvanceTimerRef.current) {
        clearTimeout(autoAdvanceTimerRef.current);
        autoAdvanceTimerRef.current = null;
      }
    };
  }, [
    scheduleStepAutoAdvance,
    currentStepIndex,
    timerFrozen,
  ]);

  useEffect(() => {
    return () => {
      if (switchPauseTimerRef.current) clearTimeout(switchPauseTimerRef.current);
      if (autoAdvanceTimerRef.current) clearTimeout(autoAdvanceTimerRef.current);
      if (voiceDescriptionTimerRef.current)
        clearTimeout(voiceDescriptionTimerRef.current);
      avatarRef.current?.stop();
    };
  }, []);

  useEffect(() => {
    if (!voiceOverEnabled || muted) return;
    if (phase !== "playing" && phase !== "switchPause") return;
    const spokenStep =
      phase === "switchPause" ? showSwitchPause ?? currentStep : currentStep;
    if (!spokenStep) return;
    const categoryLabel = getContentBankCategoryLabel(
      t,
      spokenStep.category,
      spokenStep.category
    );
    const key =
      phase === "switchPause"
        ? "games.categoryTap.voicePromptNewCategory"
        : "games.categoryTap.voicePromptCategory";
    avatarRef.current?.speak(
      t(key, {
        category: categoryLabel,
      }),
      { interrupt: true, replaceQueue: true }
    );
  }, [
    phase,
    currentStep?.category,
    showSwitchPause?.category,
    t,
    voiceOverEnabled,
    muted,
  ]);

  useEffect(() => {
    if (!voiceOverEnabled || muted) return;
    if (phase !== "playing" || !currentStep) return;
    if (voiceDescriptionTimerRef.current) {
      clearTimeout(voiceDescriptionTimerRef.current);
      voiceDescriptionTimerRef.current = null;
    }
    const voiceText = getContentBankVoiceDescription(
      t,
      currentStep.item.id,
      currentStep.item.label,
      currentStep.item.voiceDescription
    );
    if (!voiceText) return;
    voiceDescriptionTimerRef.current = setTimeout(() => {
      avatarRef.current?.speak(voiceText, {
        interrupt: false,
        // Keep prompt + previous item(s) in order; do not squash queue.
        replaceQueue: false,
      });
      voiceDescriptionTimerRef.current = null;
    }, 250);
  }, [
    currentStepIndex,
    currentStep?.item?.id,
    currentStep?.item?.voiceDescription,
    phase,
    voiceOverEnabled,
    muted,
    t,
  ]);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAtRef.current < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAtRef.current = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    startSession({ patientId: selectedPatientId })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId) {
          setSessionId(res.sessionId);
          setSessionStartTime(gameNow());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, startSession, t, gameNow]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(gameNow());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary, gameNow]);

  useEffect(() => {
    if (sessionStartTime == null) return;
    const id = setInterval(() => {
      if (isTimerFrozen()) return;
      setTimerSeconds(Math.floor((gameNow() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, gameNow, isTimerFrozen]);

  // Refresh footer timer immediately when the clock unfreezes.
  useEffect(() => {
    if (sessionStartTime == null || isTimerFrozen()) return;
    setTimerSeconds(Math.floor((gameNow() - sessionStartTime) / 1000));
  }, [sessionStartTime, timerFrozen, gameNow, isTimerFrozen]);

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      // Per-item average reaction time in seconds, averaged across rounds that
      // captured any reaction time. Falls back to 0 when no round recorded
      // anything (e.g. every item timed out).
      const reactionRounds = allResults.filter(
        (r): r is typeof r & { avgReactionMs: number } =>
          r.avgReactionMs != null,
      );
      const avgReactionSeconds =
        reactionRounds.length > 0
          ? reactionRounds.reduce((s, r) => s + r.avgReactionMs, 0) /
            reactionRounds.length /
            1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    setRoundCount(nextRound);
    startNewRound(nextRound);
  }, [
    roundCount,
    roundResults,
    sessionId,
    sessionScore,
    domainUpdatesSum,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    startNewRound,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const handleRequestMoreTime = useCallback(() => {
    if (
      moreTimeUsedForStep ||
      phase !== "playing" ||
      !roundContent ||
      roundContent.levelParams.timePerItemMs <= 0
    )
      return;
    const bonus = getInGameMoreTimeBonusMs(GAME_IDS.categoryTap);
    if (bonus <= 0) return;
    setStepBonusMs((ms) => ms + bonus);
    setMoreTimeUsedForStep(true);
  }, [moreTimeUsedForStep, phase, roundContent]);

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const feedbackText =
    phase === "feedback" ? t("games.categoryTap.feedbackGood") : null;
  const canStartRound =
    !voiceOverEnabled || (!sessionId ? false : avatarReady);

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.categoryTap.title")}
      level={difficulty}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={togglePause}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
      voiceOverEnabled={voiceOverEnabled}
      onVoiceOverChange={setVoiceOverEnabled}
      moreTime={
        inGameMoreTimeAllowed &&
        phase === "playing" &&
        (roundContent?.levelParams.timePerItemMs ?? 0) > 0
          ? {
              onRequest: handleRequestMoreTime,
              disabled: moreTimeUsedForStep,
            }
          : undefined
      }
    >
      {sessionId && (
        <GameAvatarOverlay
          ref={avatarRef}
          gameSessionId={sessionId}
          enabled={voiceOverEnabled}
          muted={muted}
          onReadyChange={setAvatarReady}
        />
      )}
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.categoryTap.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.categoryTap.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => startNewRound()}
            disabled={!canStartRound}
            sx={gameContinueButtonSx}
          >
            {t("games.categoryTap.startRound")}
          </Button>
          {voiceOverEnabled && !canStartRound ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              {t("companion.loadingMessage")}
            </Typography>
          ) : null}
        </Box>
      )}

      {(phase === "playing" || phase === "switchPause" || phase === "feedback") &&
        roundContent && (
        <>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: "1.5rem", mb: 1.5, textAlign: "center" }}
          >
            {phase === "feedback" ? (
              t("games.categoryTap.feedbackGood")
            ) : phase === "switchPause" && showSwitchPause ? (
              <Trans
                i18nKey="games.categoryTap.newCategory"
                values={{
                  category: getContentBankCategoryLabel(
                    t,
                    showSwitchPause.category,
                    showSwitchPause.category
                  ),
                }}
                components={{
                  highlight: (
                    <Box
                      component="span"
                      sx={{
                        color: "primary.main",
                        fontWeight: 800,
                        textUnderlineOffset: 3,
                      }}
                    />
                  ),
                }}
              />
            ) : currentStep ? (
              <Trans
                i18nKey="games.categoryTap.categoryPrompt"
                values={{
                  category: getContentBankCategoryLabel(
                    t,
                    currentStep.category,
                    currentStep.category
                  ),
                }}
                components={{
                  highlight: (
                    <Box
                      component="span"
                      sx={{
                        color: "primary.main",
                        fontWeight: 800,
                        textUnderlineOffset: 3,
                      }}
                    />
                  ),
                }}
              />
            ) : null}
          </Typography>

          {phase === "playing" && (
            <Box sx={{ mb: 1 }}>
              <LinearProgress
                variant="determinate"
                value={
                  roundContent.sequence.length > 0
                    ? (currentStepIndex / roundContent.sequence.length) * 100
                    : 0
                }
                color="primary"
                sx={{ height: 8, borderRadius: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                {t("games.categoryTap.itemProgress", {
                  current: currentStepIndex + 1,
                  total: roundContent.sequence.length,
                })}
              </Typography>
            </Box>
          )}

          {phase === "playing" && currentStep && (
            <Box
              sx={{
                flex: 1,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: { xs: 2.5, sm: 3 },
              }}
            >
              {(() => {
                const imageCandidates = buildImageCandidateUrls(
                  currentStep.item.imageUrl
                );
                const imageAttempt = imageAttemptById[currentStep.item.id] ?? 0;
                const imageSrc =
                  imageCandidates[
                    Math.min(imageAttempt, Math.max(0, imageCandidates.length - 1))
                  ];
                return (
                  <>
              <Box
                sx={{
                  width: { xs: "min(86vw, 340px)", sm: "min(62vw, 280px)", md: 280 },
                  minWidth: { xs: "min(86vw, 340px)", sm: "min(62vw, 280px)", md: 280 },
                  minHeight: { xs: "min(86vw, 340px)", sm: "min(62vw, 280px)", md: 280 },
                  borderRadius: 2,
                  border: "3px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "flex-start",
                  pt: { xs: 1, sm: 1 },
                  gap: { xs: 0.5, sm: 0.75 },
                  mx: "auto",
                  mb: 0,
                }}
              >
                <Box
                  sx={{
                    width: { xs: "min(80vw, 320px)", sm: "min(56vw, 250px)", md: 250 },
                    height: { xs: "min(80vw, 320px)", sm: "min(56vw, 250px)", md: 250 },
                    borderRadius: 1,
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                  }}
                >
                  {roundContent.levelParams.showImage && imageSrc ? (
                    <Box
                      component="img"
                      src={imageSrc}
                      alt=""
                      onError={() =>
                        setImageAttemptById((prev) => ({
                          ...prev,
                          [currentStep.item.id]: Math.min(
                            (prev[currentStep.item.id] ?? 0) + 1,
                            Math.max(0, imageCandidates.length - 1)
                          ),
                        }))
                      }
                      sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <Typography variant="h6" color="text.secondary">
                      {getContentBankLabel(
                        t,
                        currentStep.item.id,
                        currentStep.item.label
                      )
                        .slice(0, 1)
                        .toUpperCase()}
                    </Typography>
                  )}
                </Box>
                {roundContent.levelParams.showLabels && (
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 0,
                      fontSize: { xs: "0.98rem", sm: "0.95rem" },
                      fontWeight: 600,
                      textAlign: "center",
                      lineHeight: 1.15,
                    }}
                    color="text.secondary"
                  >
                    {getContentBankLabel(
                      t,
                      currentStep.item.id,
                      currentStep.item.label
                    )}
                  </Typography>
                )}
              </Box>
                  </>
                );
              })()}

              <Box
                display="flex"
                gap={2}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ mt: 0.5, mb: 0, alignSelf: "center" }}
              >
                <Button
                  variant="contained"
                  color="success"
                  size="large"
                  onClick={handleTap}
                  sx={gamePrimaryButtonSx}
                >
                  {t("games.categoryTap.tapButton")}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleSkip}
                  sx={gameSecondaryButtonSx}
                >
                  {t("games.categoryTap.skipButton")}
                </Button>
              </Box>

              {roundContent.levelParams.timePerItemMs > 0 && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0 }}
                >
                  {t("games.categoryTap.timePerItem", {
                    seconds: roundContent.levelParams.timePerItemMs / 1000,
                  })}
                </Typography>
              )}
            </Box>
          )}

          {phase === "switchPause" && (
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {t("games.categoryTap.getReady")}
            </Typography>
          )}

          {phase === "feedback" && (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={2}
              mt={2}
              mb={2}
            >
              {xpEarned != null && (
                <Typography variant="body2" color="primary.main">
                  +{xpEarned} XP
                </Typography>
              )}
              <Button
                variant="contained"
                size="large"
                onClick={handleNextRound}
                sx={gameContinueButtonSx}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION
                  ? t("games.categoryTap.finishSession")
                  : t("games.categoryTap.nextRound")}
              </Button>
            </Box>
          )}
        </>
      )}
    </GameScreenShell>
  );
};

export default CategoryTap;
